"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/* ============================================================
   MENU BAGIKAN

   Satu tombol ikon; diklik, muncul menu berisi tujuan berbagi.
   Dipakai di kartu carousel beranda dan di kartu daftar artikel.

   ============================================================
   >>> MENUNYA DIRENDER KE document.body, BUKAN DI TEMPATNYA <<<

   Ini bagian terpenting dari berkas ini, dan alasannya bukan selera.

   Kartu artikel di beranda berada di dalam TIGA lapis pemotong
   sekaligus:

     1. track carousel  -> overflow-x-auto
     2. <article> kartu -> overflow-hidden
     3. pembungkus foto -> overflow-hidden

   Menu biasa yang menjuntai ke bawah akan terpotong ketiganya. Yang
   sering luput: begitu satu sumbu disetel auto, spesifikasi CSS
   membuat sumbu lainnya ikut auto - jadi overflow-x-auto pada track
   juga memotong ke arah bawah. Menaikkan z-index TIDAK menolong;
   z-index tidak pernah bisa melawan overflow.

   Karena itu menunya dipindah ke document.body lewat portal, dan
   diposisikan dengan position:fixed memakai koordinat tombolnya.
   Di luar ketiga pemotong itu, ia tidak bisa dipotong siapa pun.

   KONSEKUENSINYA: koordinatnya beku. Begitu halaman atau carousel
   digulir, menu akan menggantung di tempat lamanya - jadi menu
   DITUTUP saat ada gulir. Itu bukan kemalasan, itu perilaku yang
   sama dengan menu bawaan sistem operasi.

   ============================================================
   >>> INSTAGRAM TIDAK PUNYA TAUTAN BERBAGI. TITIK. <<<

   Facebook, LinkedIn, dan WhatsApp menyediakan alamat resmi untuk
   membagikan tautan. INSTAGRAM TIDAK - tidak pernah ada, dan itu
   keputusan Meta, bukan keterbatasan kode.

   Alamat seperti instagram.com/share?url=... yang beredar di internet
   TIDAK BERFUNGSI; ia hanya membuka halaman depan atau halaman login.
   Jangan tergoda memasangnya karena "kelihatan ada": tombol yang
   membuka halaman salah lebih buruk daripada tombol yang jujur.

   Yang dipakai di sini:
     - Di komputer: tombol Instagram MENYALIN tautannya, karena itu
       memang satu-satunya cara memasukkan tautan ke Instagram -
       ditempel ke bio, atau ke stiker tautan di Story. Labelnya
       menyebutkan itu terang-terangan.
     - Di ponsel: item "Bagikan lainnya" membuka lembar berbagi bawaan
       sistem, dan DI SANA Instagram muncul sebagai pilihan. Itu
       satu-satunya jalan yang benar-benar sampai ke Instagram.

   ============================================================
   >>> PAPAN KLIP BUTUH SECURE CONTEXT <<<

   navigator.clipboard hanya ada di HTTPS atau localhost. Situs yang
   dibuka lewat alamat LAN seperti http://192.168.0.109:3005 TIDAK
   termasuk. Karena itu salinTautan() punya dua lapis cadangan.

   ⚠️ salinCaraLama() di dasar berkas ini sama dengan yang ada di
   ArtikelContent.jsx. DUA salinan masih bisa ditoleransi; kalau
   nanti muncul yang ketiga, pindahkan ke app/lib/salin.js dan impor
   dari semuanya.

   ============================================================
   >>> JANGAN TULIS className SEBAGAI STRING MULTI-BARIS. <<<
   Proyek ini berakhiran baris CRLF. String class yang dipecah
   beberapa baris ikut membawa \r\n ke dalam nilai atribut dan memicu
   hydration error.
   ============================================================ */

const LEBAR_MENU = 224;   // sama dengan w-56; dipakai untuk hitung posisi
const TINGGI_KIRA = 250;  // perkiraan, hanya untuk memutuskan buka ke atas

const TEKS = {
  bagikan: { id: "Bagikan", en: "Share" },
  tutup: { id: "Tutup menu bagikan", en: "Close share menu" },
  salin: { id: "Salin tautan", en: "Copy link" },
  tersalin: { id: "Tautan disalin", en: "Link copied" },
  instagram: { id: "Instagram (salin tautan)", en: "Instagram (copy link)" },
  igTersalin: { id: "Disalin - tempel di Instagram", en: "Copied - paste in Instagram" },
  lainnya: { id: "Bagikan lainnya", en: "More options" },
  salinManual: { id: "Salin tautan ini:", en: "Copy this link:" },
};

function pilihTeks(nilai, lang) {
  if (nilai == null) return "";
  if (typeof nilai === "string") return nilai;
  return nilai[lang] || nilai.id || "";
}

/* Lambang merek memakai fill (bidang penuh), sisanya stroke. Memang
   tidak seragam - lambang merek tidak bisa digambar ulang sebagai
   garis tanpa jadi tidak dikenali. */
function Ikon({ nama }) {
  const p = { width: 17, height: 17, viewBox: "0 0 24 24", "aria-hidden": "true" };

  if (nama === "facebook") {
    return (
      <svg {...p} fill="currentColor">
        <path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12Z" />
      </svg>
    );
  }

  if (nama === "linkedin") {
    return (
      <svg {...p} fill="currentColor">
        <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.7h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1V21h-4v-5.5c0-1.3-.03-3-1.83-3-1.83 0-2.11 1.43-2.11 2.9V21h-4V9Z" />
      </svg>
    );
  }

  if (nama === "whatsapp") {
    return (
      <svg {...p} fill="currentColor">
        <path d="M12 2a10 10 0 0 0-8.6 15L2 22l5.2-1.4A10 10 0 1 0 12 2Zm5.2 14.1c-.2.6-1.2 1.2-1.7 1.2-.4 0-1 .1-3-.8-2.5-1.1-4.1-3.7-4.2-3.9-.1-.2-1-1.3-1-2.5s.6-1.8.9-2c.2-.3.5-.3.6-.3h.5c.2 0 .4 0 .6.5l.8 1.9c.1.2.1.4 0 .6l-.4.5c-.1.2-.3.3-.1.6.1.3.7 1.2 1.5 1.9 1 .9 1.8 1.2 2.1 1.3.2.1.4.1.6-.1l.7-.8c.2-.2.4-.2.6-.1l1.8.9c.2.1.4.2.4.3.1.2.1.6-.1 1.1Z" />
      </svg>
    );
  }

  if (nama === "instagram") {
    return (
      <svg {...p} fill="none" stroke="currentColor" strokeWidth="1.9">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" stroke="none" />
      </svg>
    );
  }

  if (nama === "centang") {
    return (
      <svg {...p} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="m5 12.5 4.5 4.5L19 7" />
      </svg>
    );
  }

  if (nama === "rantai") {
    return (
      <svg {...p} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1 1" />
        <path d="M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1-1" />
      </svg>
    );
  }

  /* simpul-berbagi - dipakai untuk tombol pemicu dan "Bagikan lainnya" */
  return (
    <svg {...p} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" />
    </svg>
  );
}

export default function MenuBagikan({ url, judul = "", lang = "id", kelasTombol = "" }) {
  const [pasang, setPasang] = useState(false);
  const [buka, setBuka] = useState(false);
  const [posisi, setPosisi] = useState({ top: 0, left: 0 });
  const [tersalin, setTersalin] = useState(false);
  const [igTersalin, setIgTersalin] = useState(false);
  const [adaBagikanSistem, setAdaBagikanSistem] = useState(false);
  const [alamat, setAlamat] = useState("");

  const pemicu = useRef(null);
  const panel = useRef(null);

  /* window tidak ada di peladen. Alamat dan deteksi kemampuan browser
     WAJIB dihitung setelah komponen menempel - kalau dibaca saat
     render, `next build` gagal saat prerender. */
  useEffect(() => {
    setPasang(true);
    setAdaBagikanSistem(Boolean(navigator.share));

    if (!url) {
      setAlamat(window.location.href);
    } else if (url.startsWith("http")) {
      setAlamat(url);
    } else {
      /* Path relatif diubah jadi alamat penuh. Facebook dan LinkedIn
         menolak path relatif - mereka butuh domainnya. */
      setAlamat(new URL(url, window.location.origin).href);
    }
  }, [url]);

  const hitungPosisi = useCallback(() => {
    const el = pemicu.current;
    if (!el) return;
    const r = el.getBoundingClientRect();

    /* Dirapatkan ke kanan tombol, lalu dijepit supaya tidak keluar
       layar. Tanpa penjepitan, kartu paling kanan di carousel akan
       melempar menunya ke luar viewport. */
    const left = Math.min(
      Math.max(8, r.right - LEBAR_MENU),
      window.innerWidth - LEBAR_MENU - 8
    );

    /* Buka ke atas kalau ruang di bawah tidak cukup. */
    const keAtas = r.bottom + TINGGI_KIRA > window.innerHeight;
    const top = keAtas ? Math.max(8, r.top - TINGGI_KIRA - 8) : r.bottom + 8;

    setPosisi({ top, left });
  }, []);

  const tutup = useCallback((kembalikanFokus = true) => {
    setBuka(false);
    if (kembalikanFokus && pemicu.current) pemicu.current.focus();
  }, []);

  useEffect(() => {
    if (!buka) return;

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        tutup();
      }
    };

    const onKlik = (e) => {
      const diPemicu = pemicu.current && pemicu.current.contains(e.target);
      const diPanel = panel.current && panel.current.contains(e.target);
      if (!diPemicu && !diPanel) tutup(false);
    };

    /* Koordinat menu beku karena position:fixed. Begitu ada gulir -
       halaman maupun carousel - menu akan menggantung di tempat
       lamanya, jadi ditutup saja. capture:true supaya gulir pada
       wadah dalam ikut tertangkap, bukan hanya gulir jendela. */
    const onGulir = () => tutup(false);

    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onKlik);
    document.addEventListener("touchstart", onKlik);
    window.addEventListener("scroll", onGulir, true);
    window.addEventListener("resize", onGulir);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onKlik);
      document.removeEventListener("touchstart", onKlik);
      window.removeEventListener("scroll", onGulir, true);
      window.removeEventListener("resize", onGulir);
    };
  }, [buka, tutup]);

  const salinTautan = async (penanda) => {
    const tandai = () => {
      penanda(true);
      setTimeout(() => penanda(false), 2000);
    };

    try {
      /* Cek isSecureContext, BUKAN cuma keberadaan navigator.clipboard.
         Sebagian peramban tetap memunculkan objeknya di halaman
         non-HTTPS tapi menolak saat dipanggil. */
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(alamat);
      } else {
        salinCaraLama(alamat);
      }
      tandai();
    } catch {
      try {
        salinCaraLama(alamat);
        tandai();
      } catch {
        window.prompt(pilihTeks(TEKS.salinManual, lang), alamat);
      }
    }
  };

  const bagikanSistem = async () => {
    try {
      await navigator.share({ title: judul, url: alamat });
      tutup(false);
    } catch {
      /* Pengunjung menutup lembar berbagi. Bukan error. */
    }
  };

  const enc = encodeURIComponent(alamat);
  const encJudul = encodeURIComponent(judul);

  const DAFTAR = [
    { kunci: "facebook", nama: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc}` },
    { kunci: "whatsapp", nama: "WhatsApp", href: `https://api.whatsapp.com/send?text=${encJudul}%20${enc}` },
    { kunci: "linkedin", nama: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc}` },
  ];

  const labelBagikan = pilihTeks(TEKS.bagikan, lang);
  const kelasItem =
    "flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-semibold text-[var(--c-ink)] transition-colors hover:bg-[var(--c-tint)] focus-visible:bg-[var(--c-tint)] focus-visible:outline-none";

  const menu = (
    <div
      ref={panel}
      role="menu"
      aria-label={labelBagikan}
      style={{ position: "fixed", top: posisi.top, left: posisi.left, width: LEBAR_MENU }}
      className="z-[200] overflow-hidden rounded-xl bg-[var(--c-surface)] py-1.5 shadow-[0_4px_6px_-2px_rgba(15,23,42,0.10),0_16px_32px_-8px_rgba(15,23,42,0.24)] ring-1 ring-[var(--c-line)]"
    >
      {DAFTAR.map((item) => (
        <a
          key={item.kunci}
          role="menuitem"
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => tutup(false)}
          className={kelasItem}
        >
          <span className="shrink-0 text-[var(--c-brand)]">
            <Ikon nama={item.kunci} />
          </span>
          {item.nama}
        </a>
      ))}

      {/* INSTAGRAM - tombol, BUKAN tautan. Lihat catatan di kepala
         berkas: tidak ada alamat berbagi milik Instagram, jadi yang
         dilakukan tombol ini adalah menyalin tautannya. */}
      <button type="button" role="menuitem" onClick={() => salinTautan(setIgTersalin)} className={kelasItem}>
        <span className="shrink-0 text-[var(--c-brand)]">
          <Ikon nama={igTersalin ? "centang" : "instagram"} />
        </span>
        {igTersalin ? pilihTeks(TEKS.igTersalin, lang) : pilihTeks(TEKS.instagram, lang)}
      </button>

      <span aria-hidden="true" className="my-1.5 block h-px bg-[var(--c-line)]" />

      <button type="button" role="menuitem" onClick={() => salinTautan(setTersalin)} className={kelasItem}>
        <span className="shrink-0 text-[var(--c-brand)]">
          <Ikon nama={tersalin ? "centang" : "rantai"} />
        </span>
        {tersalin ? pilihTeks(TEKS.tersalin, lang) : pilihTeks(TEKS.salin, lang)}
      </button>

      {/* Hanya di perangkat yang punya lembar berbagi bawaan -
         praktisnya ponsel. Di sinilah Instagram benar-benar terjangkau. */}
      {adaBagikanSistem && (
        <button type="button" role="menuitem" onClick={bagikanSistem} className={kelasItem}>
          <span className="shrink-0 text-[var(--c-brand)]">
            <Ikon nama="simpul" />
          </span>
          {pilihTeks(TEKS.lainnya, lang)}
        </button>
      )}
    </div>
  );

  return (
    <>
      <button
        ref={pemicu}
        type="button"
        onClick={(e) => {
          /* WAJIB: tombol ini berada di atas area ::after milik tautan
             judul kartu. Tanpa ini, menekan bagikan justru membuka
             artikelnya. */
          e.preventDefault();
          e.stopPropagation();
          if (!buka) hitungPosisi();
          setBuka((v) => !v);
        }}
        aria-haspopup="menu"
        aria-expanded={buka}
        aria-label={buka ? pilihTeks(TEKS.tutup, lang) : labelBagikan}
        title={labelBagikan}
        className={`flex items-center justify-center rounded-full text-[var(--c-brand-strong)] transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)] ${kelasTombol}`}
      >
        <Ikon nama="simpul" />
      </button>

      {pasang && buka && alamat && createPortal(menu, document.body)}
    </>
  );
}

/* ============================================================
   SALIN CARA LAMA - document.execCommand("copy")

   Usang, dan tetap dipakai dengan sadar: ia satu-satunya cara
   menyalin di halaman non-HTTPS, dan masih didukung seluruh peramban
   arus utama.

   Textarea WAJIB menempel di dokumen sebelum bisa disorot - elemen
   lepas tidak bisa di-select(). position fixed + opacity 0 supaya
   halaman tidak melompat, readonly supaya papan ketik virtual di
   ponsel tidak ikut naik.

   Melempar error kalau gagal, BUKAN mengembalikan false - itu yang
   membuat catch di salinTautan bisa turun ke lapis prompt.
   ============================================================ */
function salinCaraLama(teks) {
  const ta = document.createElement("textarea");
  ta.value = teks;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "0";
  ta.style.left = "0";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  ta.setSelectionRange(0, teks.length);
  const berhasil = document.execCommand("copy");
  document.body.removeChild(ta);
  if (!berhasil) throw new Error("execCommand copy gagal");
}
